import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// CellContextMenu.tsx — the right-click menu over the board. Pure presentation: it knows where to sit and
// which rows to draw, the page decides what the rows mean. The "count" row carries its own number box so
// "duplicate this 5 times" needs no dialog — a browser prompt would freeze the Unity iframe next to us.
import { Component } from "react";
import { NumberInput } from "./NumberInput";
const MENU_WIDTH = 190;
const ROW_HEIGHT = 28;
// The row carrying the number box is taller than a plain label row; measuring every row at ROW_HEIGHT
// under-estimated the menu and let it hang a few pixels past the bottom of a short viewport.
const COUNT_ROW_HEIGHT = 34;
export class CellContextMenu extends Component {
    constructor() {
        super(...arguments);
        // A click anywhere outside the menu dismisses it, including on the canvas underneath.
        this.onDocumentMouseDown = (e) => {
            if (e.target instanceof Node && this.root && this.root.contains(e.target)) {
                return;
            }
            // Stopped here, in the capture phase, so the press that dismisses the menu does not go on to reach
            // the board: React's root listener sits below document, and a left-click on an empty cell PLACES
            // the active mechanic — so clicking away to close the menu quietly authored a box, and dragging
            // away painted a row of them.
            e.stopPropagation();
            this.props.onClose();
        };
        this.root = null;
    }
    componentDidMount() {
        document.addEventListener("mousedown", this.onDocumentMouseDown, true);
    }
    componentWillUnmount() {
        document.removeEventListener("mousedown", this.onDocumentMouseDown, true);
    }
    render() {
        const { screenX, screenY, items, count, onCountChange, onSelect } = this.props;
        // Keep the menu on screen: flip it left/up when it would run past the viewport edge, AND clamp it
        // back down/right when the viewport is narrower/shorter than the menu itself (this pane sits beside
        // the Unity iframe, so it does not get the whole browser window) — without the lower bound, a
        // right-click near the top-left corner of a cramped viewport could push `left`/`top` negative and
        // strand the menu partly or entirely off-screen with no way to reach the clipped rows.
        const height = items.reduce((h, it) => h + (it.kind === "count" ? COUNT_ROW_HEIGHT : ROW_HEIGHT), 0) + 8;
        const left = Math.max(8, Math.min(screenX, window.innerWidth - MENU_WIDTH - 8));
        const top = Math.max(8, Math.min(screenY, window.innerHeight - height - 8));
        return (_jsx("div", { ref: (el) => { this.root = el; }, style: { position: "fixed", left, top, width: MENU_WIDTH, zIndex: 50 }, className: "rounded-md border border-slate-700 bg-slate-900 py-1 shadow-xl", children: items.map((it) => it.kind === "count" ? (
            // Enter in the number box runs the row. Without it the only way to apply the count
            // was to type it and then reach back for the label beside it, which reads as a
            // dead end — you type a number and nothing happens.
            _jsxs("div", { onKeyDown: (e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onSelect(it.id);
                    }
                }, className: "flex items-center gap-1.5 px-2 py-1 text-xs text-slate-300", children: [_jsx("button", { type: "button", onClick: () => onSelect(it.id), className: "flex-1 truncate rounded px-1 py-0.5 text-left hover:bg-slate-800", children: it.label }), _jsx(NumberInput, { value: count, min: 1, max: 49, onChange: onCountChange, ariaLabel: "How many duplicates", className: "w-10 rounded border border-slate-700 bg-slate-800 px-1 py-0.5 text-xs text-slate-100 focus:border-indigo-500 focus:outline-none" })] }, it.id)) : (_jsx("button", { type: "button", onClick: () => onSelect(it.id), className: "block w-full px-3 py-1 text-left text-xs text-slate-300 hover:bg-slate-800 hover:text-white", children: it.label }, it.id))) }));
    }
}
